/**
 * THE ASTRO PRODUCT — Dhanabhyas. Vedic and astrological charting, and nothing quantitative.
 *
 * <p>Every id below is looked up in {@code toolRegistry.js}; an id this file does not name is
 * never mounted. That is the whole mechanism — there is no deny list, no exemption table and no
 * catalog mode, so a tool added to the registry reaches this product only by being added here.
 *
 * <h2>⚠ WHAT THIS PRODUCT NO LONGER CARRIES, and it is a long list</h2>
 *
 * The brief is explicit: "zero playback controls, zero quantitative CTS/PTA/PTB scan pills". Held
 * literally — which is how it is held — that also removes everything a reader reaches THROUGH
 * those engines, because none of it is astrological:
 *
 * <ul>
 *   <li>the three CTS orb scans, PTA, PTB and the three engine runners;</li>
 *   <li>TrendPulse, the Φ reversal scan, fixed-length cycles, the global scan, Auto-Analysis;</li>
 *   <li>the Visual Universe, Chart Analytica, the Market Scanner, the Portfolio Manager, Alpha
 *       Replay, the DynaMan Simulator and the Workbench;</li>
 *   <li>SK360, Time Projection, SK-Grid and the pivot/Fibonacci projections — price-and-time
 *       geometry, which is Gann rather than Jyotish;</li>
 *   <li>the six unbuilt placeholder buttons — not by being denied, but because they carry no
 *       action id, so no manifest can name them and the toolbar never mounts them.</li>
 * </ul>
 *
 * <p>⚠ THE FOUR STRATEGY DESIGNERS WERE ON THAT LIST UNTIL 2026-11-03 and are now on the toolbar,
 * on instruction. The reasoning for excluding them was that a reader reaches them THROUGH the
 * quant engines; that is not so — they combine the astrological studies this product already
 * carries, and Strategy Designer 2 is explicitly a Vedic one. The exclusion was the allow-list
 * being read more literally than it was meant, which is the same correction `cycles_dialog`
 * needed two days earlier.
 *
 * <p>That is a deliberate narrowing from the sixty-odd controls this deployment showed before,
 * and it is the point of an allow-list: the product is now what this file says and not what
 * nobody got round to denying.
 *
 * <h2>⚠ AND THE COLOUR DIALOG IS GONE AGAIN — `configuration`, removed 2026-11-03 on instruction</h2>
 *
 * It was RELOCATED to the Admin Panel, found unreachable for customers because a customer product
 * has no Admin Panel, and deliberately restored to this toolbar on 2026-11-01 —
 * `adminPanelTools.js` records that reversal as "a restored CUSTOMER capability". Removing the id
 * here puts it back out of reach by a different route.
 *
 * <p>⚠ LOADING A SAVED PREFERENCE IS UNAFFECTED, which the instruction asked about specifically.
 * `App` calls `readWorkspaceColors()` in a `useState` initialiser at mount and subscribes through
 * `onWorkspaceColorsChange`, neither of which is gated on the toolbar or on the dialog being
 * mounted; `ConfigurationDialog` is the only WRITER. So stored colours keep being read and applied.
 *
 * <p>⚠ WHAT IS LOST IS CHANGING OR CLEARING THEM. A customer who has already saved a custom PTA,
 * PTB or CTS colour now keeps it applied indefinitely with no surface to edit or reset it — and
 * `writeWorkspaceColors` is what removes the key when the last override is cleared, so even
 * reverting to the defaults is unreachable. Restoring it is one id in `VIEW_CHROME` below.
 * integralDelta and the Studio keep theirs; each manifest has its own copy of that block.
 *
 * <h2>⚠ AND NEITHER MESSAGE SURFACE — there are TWO, and they are easy to conflate</h2>
 *
 * Confirmed absent 2026-11-02, from every customer manifest, deliberately:
 *
 * <ul>
 *   <li>{@code message_centre} → dispatches {@code open_messages_dialog}. The read-only notice
 *       board: what the team publishes and a customer reads.</li>
 *   <li>{@code messages_panel} → dispatches {@code open_messages_panel}. The STUDY-OUTPUT panel —
 *       Summary / Journal / Reserved 1 / Reserved 2 — which is where a study's own text lands.</li>
 * </ul>
 *
 * <p>The two are a tool id apart and are routinely referred to by one name; a brief naming
 * "{@code open_messages_panel} (Message Center)" names the ACTION of the second and the LABEL of
 * the first. Both are Admin-Tab-3 only, so the requirement is met under either reading — stated
 * here rather than left to whichever one a later reader assumes.
 *
 * <p>⚠ THE MESSAGE CENTRE'S ABSENCE HAS A RECORDED COST: it is a customer's only route to a
 * published notice, and `CUSTOMER_FACING_BY_PRODUCT[integraldelta]` still lifts the ROLE gate on
 * it — so the manifest is the only thing withholding it, and restoring it is one id. The
 * study-output panel has no such cost: it shows a study's output and this product's studies write
 * their results to the chart.
 */

/*
 * ⚠ THE VIEW AND EXPORT CHROME IS INCLUDED, and the brief does not name it.
 *
 * Stated rather than quietly added. The brief's list is tools; `chart_type`, `area_zoom`,
 * `save_screenshot` and `copy_chart` are how a reader changes the chart to candles, zooms a
 * region and saves the picture. Omitting them produces a chart that cannot be exported, which no
 * reading of "remove the quant pills" asks for — so they ship, in their own block, and removing
 * them is deleting four lines.
 *
 * <p>⚠ `configuration` LEFT THIS BLOCK ON 2026-11-03, on instruction — the PTA/PTB/CTS colour
 * dialog. See the toolbar's own note below for what that costs, because it is not nothing and it
 * reverses a decision taken two days earlier.
 *
 * <p>⚠ `watermark_toggle` IS NOT AMONG THEM, and it was until a test caught it. That control is
 * `props.isAdmin &&` gated in the toolbar, so a customer manifest naming it claimed a capability
 * the role gate withholds — a manifest that is not the product definition, which is the one
 * thing this architecture may not be. It lives in the admin manifest alone.
 */
const VIEW_CHROME = Object.freeze([
  'chart_type',
  'area_zoom',
  'save_screenshot',
  'copy_chart',
  /*
   * ⚠ THE STUDY-OUTPUT TOGGLE, EXTENDED TO astro 2026-11-03 — widening one commit's deliberate
   * scoping to integralDelta alone.
   *
   * That scoping was defensible and is reversed on instruction: integralDelta is where the
   * studies are run, so it got the bar control and astro kept the header one. What it cost is
   * PARITY — a reader moving between the two products met the same drawer in two different
   * places, and the toolbar strip is where a reader's hand already is when a study finishes.
   *
   * <p>It is still a SECOND route, not the first: `MessagesPanelButton` remains in the header on
   * all four products. Nothing is removed here.
   *
   * <p>⚠ A MANIFEST ENTRY ALONE WOULD DO NOTHING — `messages_panel` has no `ICONS` row, so the
   * membership filter has nothing to admit. The control is hand-written JSX in `TabPanel`, gated
   * by `showsTool`, next to `area_zoom` and `clear_all`.
   */
  'messages_panel',
]);

/** The pens. Shared verbatim with integralDelta — a trendline is a trendline. */
export const CORE_DRAWING = Object.freeze([
  'draw_trendline',
  'draw_extended_trendline',
  'draw_horizontal_line',
  'draw_vertical_line',
  'draw_vertical_full',
  'draw_rectangle',
  'draw_circle_empty',
  'draw_circle_filled',
  'draw_fibonacci',
  'draw_fib_time',
  'draw_pitchfork',
  'draw_text',
]);

/** The three indicators plus the S&R dialog, shared with integralDelta for the same reason. */
export const CORE_INDICATORS = Object.freeze([
  'rsi',
  'macd',
  'support_resistance',
  'moving_averages',
]);

/** X1 then X — undo one drawing, then clear the chart. In that order on screen. */
export const CLEAR_ACTIONS = Object.freeze(['undo_last', 'clear_all']);

export const astroManifest = Object.freeze({
  id: 'astro',

  brandName: 'Dhanabhyas',
  watermark: 'Dhanabhyas',

  /*
   * ⚠ THE DHANABHYAS DOMAINS ARE NOT HERE, and the brief put them here.
   *
   * `productTarget.js` already carries a FOURTH target — `DHANABHYAS`, the India deployment at
   * app.dhanabhyas.com — deliberately separate from astro so the two can diverge. Claiming its
   * hostnames here would resolve that product to this manifest and quietly merge the pair; it
   * has its own file, `dhanabhyas.manifest.js`, and the renaming of ASTRO's brand to
   * "Dhanabhyas" is a branding change on astrostockcharts.com rather than a change of domain.
   *
   * <p>Both Firebase Hosting default domains are listed, because Hosting serves the same site on
   * `.web.app` AND `.firebaseapp.com` and listing one locks out anyone using the other — the
   * same rule the browser key's referrer list is documented under.
   */
  domains: Object.freeze([
    'astrostockcharts.com',
    'www.astrostockcharts.com',
    'astrostockcharts-site-prod.web.app',
    'astrostockcharts-site-prod.firebaseapp.com',
  ]),

  /*
   * ⚠ THIS IS A SET, NOT AN ORDER — the comment here claimed "rendered in this order, left to
   * right" and that was never true.
   *
   * `TabPanel` renders `ICONS.map(...)` and uses the manifest only as a membership filter through
   * `showsAction`, so WHAT appears is this file's business and WHERE it appears is the ICONS
   * array's. The grouping below is for a reader of this file; moving an id between groups changes
   * nothing on screen.
   *
   * <p>Worth knowing before anybody is asked to "move a tool along the toolbar": that is an edit
   * to `ICONS` in `TabPanel.js`, and it moves the tool for every product at once.
   */
  toolbar: Object.freeze([
    /* Ephemeris lines */
    'planetary_lines',
    'average_longitude',

    /* Navamsha / sidereal overlays */
    'longitude_study',
    'declination_study',
    'degree_range_highlight',
    'area_highlight',

    /* Aspect markers */
    'planet_follow',
    'planet_characteristics',

    /*
     * ⚠ THE FOUR STRATEGY STUDIES — ADDED 2026-11-03, reversing the exclusion this file's own
     * docblock used to record.
     *
     * The brief calls them "Strategy Designer 1 through 4" and only two are literally named that;
     * the four flask icons are `strategy_1`, `strategy_2`, `time_event_strategy` and
     * `moa_strategy`, declared as one block in the registry and contiguous in `ICONS`, so they
     * render as a group of four whatever this file's grouping says.
     *
     * <p>They belong on an astrological product on their own merits rather than by assignment:
     * Strategy Designer 2's own tooltip reads "more focused on providing Vedic Astrology studies",
     * and all four COMBINE the studies above them into a single criterion set. That is why they
     * sit after the aspect markers here — the studies, then the builders that consume them.
     *
     * <p>None of the four is role-gated, relocated, or `adminOnly` in `ICONS`, which is what makes
     * naming them legitimate: a customer manifest may only claim a capability the role gate
     * actually grants, and `watermark_toggle` is the recorded counter-example.
     */
    'strategy_1',
    'strategy_2',
    'time_event_strategy',
    'moa_strategy',

    /* Reference */
    'astro_catalog_notes',
    'intraday_animation',

    ...CORE_DRAWING,

    /*
     * ⚠ RESTORED 2026-11-01, having left the bar in Stage 1 as a consequence of the brief's
     * explicit tool lists. It is standard chart functionality — zoom in, click the two bars that
     * define a cycle, and the dialog draws it — not a research instrument, so withholding it from
     * a customer was the allow-list being read more literally than it was meant.
     */
    'cycles_dialog',
    ...CORE_INDICATORS,
    ...VIEW_CHROME,
    ...CLEAR_ACTIONS,
  ]),

  /**
   * ⚠ NULL, NOT AN EMPTY OBJECT. A customer product has no Admin Panel at all, so the button is
   * not in the toolbar above and the modal is never constructed. An empty tab list would render
   * a dialog with nothing in it, which is a worse answer than no dialog.
   */
  adminPanel: null,
});

export default astroManifest;
